import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, Locale } from '../i18n/translations';

interface LanguageContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: React.ReactNode;
  defaultLocale?: Locale;
  storageKey?: string;
}

export function LanguageProvider({
  children,
  defaultLocale = 'pt-BR',
  storageKey = 'paladino-locale',
}: LanguageProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = localStorage.getItem(storageKey) as Locale;
    return stored && translations[stored] ? stored : defaultLocale;
  });

  useEffect(() => {
    localStorage.setItem(storageKey, locale);
    // Update document lang attribute
    document.documentElement.lang = locale;
  }, [locale, storageKey]);

  const setLocale = useCallback((newLocale: Locale) => {
    if (translations[newLocale]) {
      setLocaleState(newLocale);
    }
  }, []);

  const t = useCallback(
    (key: string): string => {
      const keys = key.split('.');
      let value: any = translations[locale];

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          console.warn(`Translation key not found: ${key} for locale ${locale}`);
          return key;
        }
      }

      return typeof value === 'string' ? value : key;
    },
    [locale]
  );

  return (
    <LanguageContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
